import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {AuthService} from '@app/core/services/auth.service';
import {BaseService} from '@app/core/services/base.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {FileEntry} from '@app/core/models/fileEntry.model';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {forkJoin, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {Pagination} from '@app/core/models/pagination.model';

@Injectable()
export class FileEntryService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): FileEntry {
		if (!params) {
			return new FileEntry(this.tenantTZ, this.interService);
		}

		return new FileEntry(this.tenantTZ, this.interService).deserialize(params);
	}

	public downloadFile(id: number): void {
		const newTab = window.open('', '', '');
		this.subscriptions.push(this.httpService.getJson('fileEntries/' + id).subscribe((res: any) => {
			newTab.location.href = res.url;
		}));
	}

	public downloadFilePunchUser(id: number): void {
		const newTab = window.open('', '', '');
		this.subscriptions.push(this.httpService.getJson('punchUser/fileEntries/' + id).subscribe((res: any) => {
			newTab.location.href = res.url;
		}));
	}

	public uploadFiles(files: File[]): Observable<FileEntry[]> {
		return forkJoin(
			files.map(file => this.uploadFile(file))
		);
	}

	public uploadFile(file: File): Observable<FileEntry> {
		return this.getSignedUrl(file).pipe(
			switchMap(
				signedUrlRes => this.uploadFileToS3(signedUrlRes.url, file).pipe(
					switchMap(
						() => this.markFileAsUploaded<any>(signedUrlRes.id).pipe(
							map(fileEntry => this.fresh(fileEntry))
						)
					)
				)
			)
		);
	}

	private markFileAsUploaded<T>(fileEntryId: number): Observable<T> {
		return this.httpService.patchJson<T>('fileEntries/' + fileEntryId, {
			state: 'UPLOADED',
		});
	}

	private uploadFileToS3(signedUrl: string, file: File): Observable<void> {
		return this.httpService.putExternal<void>(signedUrl, file);
	}

	private getSignedUrl(file: File): Observable<{ id: number, url: string }> {
		const fullFileName = file.name;
		const fileExtension = fullFileName.split('.').pop();
		const fileName = fullFileName.replace('.' + fileExtension, '');

		return this.httpService.getJson<{ id: number, url: string }>('fileEntries/signedUrl', {
			original_file_name: fileName,
			extension: fileExtension,
		});
	}

	public getFileEntryObs(id: number): Observable<FileEntry> {
		const formData: FormData = new FormData();

		return this.httpService.getJson<FileEntry>('fileEntries/' + id, formData).pipe(map(res => this.fresh(res)));
	}

	public getFileEntries(page: number, pageSize: number, isNotAttached: boolean = true): Observable<any> {
		return this.httpService.getJson<PaginationHolder<FileEntry>>('fileEntries', {
			page,
			page_size: pageSize,
			sort: '-created_at',
			'filter[is_not_attached]': isNotAttached,
		}).pipe(map((res: any) => {
			res.data = res.data.map(d => this.fresh(d));
			res.pagination = new Pagination().deserialize(res.pagination).getQOPagination();

			return res;
		}));
	}
}
